import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from './AppDialog';
import {
  Refresh as RefreshIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useHelp } from '../../contexts/HelpContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface HelpSettingsProps {
  open: boolean;
  onClose: () => void;
}

const HelpSettings: React.FC<HelpSettingsProps> = ({ open, onClose }) => {
  const { showHelp, toggleHelp, resetAllHelp } = useHelp();
  const { t } = useLanguage();

  const handleResetHelp = () => {
    resetAllHelp();
  };

  const getHelpItems = () => {
    return [
      {
        id: 'inventory-management',
        title: t('inventory.manageInventory'),
        description: t('help.inventoryManagementDesc')
      },
      {
        id: 'batch-tracking',
        title: t('inventory.batchTracking'),
        description: t('help.batchTrackingDesc')
      },
      {
        id: 'add-customer',
        title: t('customers.addCustomer'),
        description: t('help.addCustomerDesc')
      },
      {
        id: 'add-item',
        title: t('items.addItem'),
        description: t('help.addItemDesc')
      },
      {
        id: 'user-management',
        title: t('users.management'),
        description: t('help.userManagementDesc')
      },
      {
        id: 'debt-management',
        title: t('debts.title'),
        description: t('help.debtManagementDesc')
      }
    ];
  };

  return (
    <AppDialog presentation="workflow" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <AppDialogHeader icon={<HelpIcon />}>{t('help.helpSettings')}</AppDialogHeader>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showHelp}
                onChange={toggleHelp}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {t('help.toggleHelp')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('help.contextualHelpDescription')}
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('help.availableTopics')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('help.topicsDescription')}
          </Typography>
        </Box>

        <List dense>
          {getHelpItems().map((item) => (
            <ListItem key={item.id}>
              <ListItemText
                primary={item.title}
                secondary={item.description}
              />
            </ListItem>
          ))}
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('help.disableInfo')}
          </Typography>
        </Alert>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetHelp}
            size="small"
          >
            {t('help.resetHelp')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('help.resetDescription')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default HelpSettings;
